/**
 * A poster at full screen.
 *
 * The art is the one thing in a row that rewards a closer look and the one
 * thing the layout has no room for, so it gets the whole screen on a tap. It
 * is a viewer, not a gallery: tapping anywhere, pressing Escape, or the close
 * control all dismiss it.
 *
 * Rendered into the body through a portal. Left where it is declared it would
 * be laid out inside the sheet, which is both positioned and transformed —
 * either of which makes it the containing block for a fixed child, so the
 * "full screen" viewer would be trapped in the sheet's own box.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";

export function PosterViewer({
  src,
  title,
  onClose,
}: {
  src: string;
  title: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} poster`}
      onClick={onClose}
    >
      <img src={src} alt={`${title} poster`} decoding="async" />
      <button type="button" className="viewer-close" onClick={onClose}>
        Close
      </button>
    </div>,
    document.body,
  );
}
